import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { Link } from 'react-router';
import { cn } from '@/lib/cn';
import { Spinner } from './Spinner';

export type ButtonVariant =
  'primary' | 'secondary' | 'ghost' | 'danger' | 'dark' | 'toolbar' | 'toolbar-strong';
export type ButtonSize = 'sm' | 'md' | 'lg';

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-white font-semibold hover:bg-accent-hover hover:text-white',
  secondary: 'bg-surface text-ink font-medium border border-line-strong hover:border-ink hover:text-ink',
  ghost: 'bg-transparent text-ink font-medium hover:bg-surface-muted hover:text-ink',
  danger:
    'bg-surface text-danger-ink font-medium border border-danger hover:bg-danger-soft hover:text-danger-ink',
  dark: 'bg-ink text-white font-medium hover:bg-ink-hover hover:text-white',
  // Editor tab bar (code-chrome background).
  toolbar:
    'bg-transparent text-code-text border border-code-control hover:border-code-text hover:text-code-text',
  'toolbar-strong':
    'bg-transparent text-white font-medium border border-code-text hover:bg-code-control hover:text-white',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'min-h-9 px-3.5 text-sm gap-2',
  md: 'min-h-11 px-[18px] text-body-app gap-2',
  lg: 'min-h-[52px] px-6 text-base gap-2.5',
};

type CommonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Icon shown after the label (e.g. ArrowRight). */
  iconRight?: ReactNode;
  iconLeft?: ReactNode;
  fullWidth?: boolean;
  className?: string;
  children: ReactNode;
};

type AsButton = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> & {
    to?: never;
    href?: never;
    ref?: Ref<HTMLButtonElement>;
  };
type AsLink = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children' | 'href'> & {
    to: string;
    href?: never;
    disabled?: never;
    ref?: Ref<HTMLAnchorElement>;
  };
type AsAnchor = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children'> & {
    href: string;
    to?: never;
    disabled?: never;
    ref?: Ref<HTMLAnchorElement>;
  };

export type ButtonProps = AsButton | AsLink | AsAnchor;

/** Renders a Router Link with `to`, an anchor with `href`, otherwise `<button type="button">`. */
export function Button(props: ButtonProps) {
  const {
    variant = 'primary',
    size = 'md',
    loading = false,
    iconRight,
    iconLeft,
    fullWidth,
    className,
    children,
    ...rest
  } = props;
  const classes = cn(
    'inline-flex items-center justify-center rounded-md no-underline whitespace-nowrap select-none',
    'transition-colors duration-100 cursor-pointer',
    'disabled:opacity-40 disabled:cursor-not-allowed aria-disabled:opacity-40 aria-disabled:cursor-not-allowed',
    variants[variant],
    sizes[size],
    fullWidth && 'w-full',
    className,
  );
  const content = (
    <>
      {loading ? <Spinner /> : iconLeft}
      <span>{children}</span>
      {!loading && iconRight}
    </>
  );

  if ('to' in rest && rest.to !== undefined) {
    const { to, ref, ...anchor } = rest as Omit<AsLink, keyof CommonProps>;
    return (
      <Link to={to} ref={ref} className={classes} {...anchor}>
        {content}
      </Link>
    );
  }
  if ('href' in rest && rest.href !== undefined) {
    const { ref, ...anchor } = rest as Omit<AsAnchor, keyof CommonProps>;
    return (
      <a ref={ref} className={classes} {...anchor}>
        {content}
      </a>
    );
  }
  const { ref, type = 'button', disabled, ...button } = rest as Omit<AsButton, keyof CommonProps>;
  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...button}
    >
      {content}
    </button>
  );
}
